import { useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { formatIntToCurrency } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { endOfMonth, format } from "date-fns";
import { useTranslation } from "react-i18next";

import type { MonthReport } from "~/managers/use-register-manager";
import type { ColumnDefWithTotal } from "~/types";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import PDFViewer from "~/components/miscellaneous/pdf-viewer";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { api } from "~/lib/api";
import { useRegisterManager } from "~/managers/use-register-manager";
import { useReportManager } from "~/managers/use-report-manager";
import { useReportStore } from "~/stores/use-report-store";

export const Route = createFileRoute("/_main/reports/_layout/month")({
  component: MonthReports,
});

function MonthReports() {
  const { range } = useReportStore();
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const { printMonthReport, downloadMonthReport } = useRegisterManager();
  const { generateReport } = useReportManager();

  const { t } = useTranslation("common");

  const handlePdf = async (report: MonthReport) => {
    await downloadMonthReport(report);
    // setPdfBlob(blob);
  };

  const { data: reportData } = api.report.getMonthReport.useQuery({
    start: range.from,
    end: range.to,
  });

  const transformedData = Object.entries(reportData ?? {}).map(
    ([monthKey, { monthReport, dayReports }]) => ({
      monthReport,
      monthKey,
      dayReports,
    }),
  );

  const columns: ColumnDefWithTotal<MonthReport>[] = [
    {
      id: t("Report.date_from"),
      accessorKey: "date",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        return (
          rowA.original.monthReport.date.getTime() -
          rowB.original.monthReport.date.getTime()
        );
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.date_from")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{format(report.monthReport.date, "P")}</p>;
      },
    },
    {
      id: t("Report.date_until"),
      accessorKey: "date",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        return (
          rowA.original.monthReport.date.getTime() -
          rowB.original.monthReport.date.getTime()
        );
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.date_until")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{format(endOfMonth(report.monthReport.date), "P")}</p>;
      },
    },
    {
      id: t("Report.number_of_receipts"),
      accessorKey: "numberOfReceipts",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.number_of_receipts")}
        />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.monthReport.numberOfReceipts}</p>;
      },
    },
    {
      id: t("Report.customers"),
      accessorKey: "customers",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.customers")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{report.monthReport.customers}</p>;
      },
    },
    {
      id: t("Report.revenue"),
      accessorKey: "revenue",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.revenue")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{formatIntToCurrency(report.monthReport.revenue, false)}</p>;
      },
    },
    {
      id: t("Report.tips"),
      accessorKey: "tips",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.tips")} />
      ),
      cell: ({ row: { original: report } }) => {
        return <p>{formatIntToCurrency(report.monthReport.tips, false)}</p>;
      },
    },
    {
      id: t("Report.vat"),
      accessorKey: "number",
      filterFn: "includesString",
      size: 10,
      enableTotal: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.vat")} />
      ),
      cell: ({ row: { original: report } }) => {
        return (
          <p>
            {Object.entries(report.monthReport.vat)
              .map(
                ([rate, value]) =>
                  `[${rate}%: ${formatIntToCurrency(value, false)}]`,
              )
              .join(", ")}
          </p>
        );
      },
    },
    {
      id: "Actions",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Actions" />
      ),
      cell: ({ row: { original: report } }) => {
        return (
          <div className="flex flex-row gap-2 items-center">
            <button onClick={() => printMonthReport(report)}>Print</button>
            <button onClick={() => handlePdf(report)}>Download</button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Button
        onClick={() => {
          void generateReport({
            start: range.from ?? new Date(),
            end: range.to ?? new Date(),
            dayReports: transformedData.flatMap(({ dayReports }) => dayReports),
            monthReports: transformedData.flatMap(
              ({ monthReport }) => monthReport,
            ),
          });
        }}
      >
        Generate summary
      </Button>
      <DataTable
        defaultSorting={[{ id: t("Report.date_from"), desc: false }]}
        columns={columns}
        data={transformedData}
      />
      <Dialog
        open={pdfBlob !== null}
        onOpenChange={(open) => {
          if (open === false) {
            setPdfBlob(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl w-full">
          <DialogHeader>
            <DialogTitle>PDF</DialogTitle>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          {pdfBlob && <PDFViewer pdfBlob={pdfBlob} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
